// src/utils/cashflowDisplay.js
// Shared column ordering + formatting for the cashflow table (desktop AG Grid
// in CashflowPanel) and its mobile counterpart (MobileCashflowTable), so the
// monthly/yearly toggle and the new columns look consistent on both.
//
// WRITEDOWN, INTEREST_SHORTFALL and PREPAY_PENALTY are genuinely 0.00 for
// most bonds we hold (production check: PREPAY_PENALTY is non-zero in
// ~0.002% of INTEX.CASHFLOW rows) - showing three all-zero columns on every
// cashflow view would bury the rare bond where they matter. So, same idea as
// the analytics table's new-tranche gating in analyticsRows.js: derive which
// of those columns are worth showing from the rows actually displayed,
// instead of a user toggle. COUPON is always populated and is never gated.

// Canonical order once WRITEDOWN/INTEREST_SHORTFALL/PREPAY_PENALTY/COUPON are
// added after the existing PRINCIPAL/INTEREST/CASHFLOW/BALANCE group. DATE and
// YEAR share a slot: monthly rows lead with DATE instead of YEAR.
const CASHFLOW_FIELD_ORDER = [
  'CUSIP',
  'YEAR',
  'DATE',
  'PRINCIPAL',
  'INTEREST',
  'CASHFLOW',
  'BALANCE',
  'WRITEDOWN',
  'INTEREST_SHORTFALL',
  'PREPAY_PENALTY',
  'COUPON',
  'RUN_DATE',
  'TRANCHE',
];

// Subject to the gate below. COUPON is deliberately excluded - it is always
// populated, so it is always shown.
const GATED_FIELDS = Object.freeze(['WRITEDOWN', 'INTEREST_SHORTFALL', 'PREPAY_PENALTY']);

// Null and zero are different facts about a gated column: null means this run
// never computed the field (true for ~25% of stored history, runs before
// these fields existed), zero means it was computed and is zero. Only a real,
// non-zero value earns the column its place in the table.
const isMeaningfulValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const numeric = typeof value === 'number' ? value : Number(value);
  return !Number.isNaN(numeric) && numeric !== 0;
};

// Whether `field` has at least one non-null, non-zero value across the rows
// currently displayed. Evaluated against the displayed rows (not the full
// dataset) so a tranche filter or a grain switch re-evaluates the gate.
export const columnHasData = (rows, field) =>
  (Array.isArray(rows) ? rows : []).some((row) => isMeaningfulValue(row?.[field]));

// Orders the fields present in a set of cashflow rows for display, gating out
// any of GATED_FIELDS that carry nothing worth showing. A monthly row carries
// a DATE field (per the API contract); when present, DATE leads instead of
// YEAR. Any field not in the canonical order is kept, appended after the
// known ones, so older or newer source columns degrade gracefully instead of
// disappearing.
export const orderCashflowFields = (rows) => {
  const data = Array.isArray(rows) ? rows : [];
  const fields = data.length ? Object.keys(data[0] || {}) : [];
  const isMonthly = fields.includes('DATE');
  const visible = fields.filter((f) => {
    if (isMonthly && f === 'YEAR') return false;
    if (GATED_FIELDS.includes(f) && !columnHasData(data, f)) return false;
    return true;
  });
  const known = CASHFLOW_FIELD_ORDER.filter(f => visible.includes(f));
  const unknown = visible.filter(f => !CASHFLOW_FIELD_ORDER.includes(f));
  return [...known, ...unknown];
};

export const isCouponField = (field) => String(field).toUpperCase() === 'COUPON';

// COUPON is the effective coupon as a rate in percent (e.g. 5.25 -> "5.250%"),
// never a dollar amount, and must never be summed in any footer/total row.
export const formatCouponPercent = (value) => `${Number(value).toFixed(3)}%`;

// Chronological sort key for a cashflowMonthly period, formatted 'MM/DD/YYYY'
// per the API contract. A lexical or numeric sort on the string itself
// interleaves years ("01/15/2027" sorts before "06/15/2026"); parsing to
// YYYYMMDD orders periods by actual calendar date across a year boundary.
export const monthlyDateSortKey = (dateStr) => {
  const [month, day, year] = String(dateStr).split('/').map(Number);
  return year * 10000 + month * 100 + day;
};
